<template>
  <div v-if="reportData.length > 0" class="graph-container">
    <div class="graphs">
      <div class="chart-wrapper full-width">
        <div class="chart-header">
          <h3>Source Distribution Over Time</h3>
          <Dropdown
            :options="[
              { text: 'Daily', value: 'daily' },
              { text: 'Weekly', value: 'weekly' },
              { text: 'Monthly', value: 'monthly' },
            ]"
            v-model="timePeriod" />
        </div>
        <Chart type="line" :data="dailySourceChartData" :options="lineChartOptions" ref="dailySourceChart" />
      </div>
      <div class="chart-wrapper half-width">
        <h3>Average Interactions by Source</h3>
        <Chart type="bar" :data="avgInteractionsBySourceData" :options="barChartOptions" ref="avgInteractionsChart" />
      </div>
      <div class="chart-wrapper half-width">
        <h3>Average Lead Duration by Source (Days)</h3>
        <Chart type="bar" :data="avgLeadDurationBySourceData" :options="durationBarChartOptions" ref="avgDurationChart" />
      </div>
      <div class="chart-wrapper third-width">
        <h3>Source Distribution</h3>
        <Chart type="doughnut" :data="sourceChartData" :options="doughnetChartOptions" ref="sourceChart" />
      </div>
      <div class="chart-wrapper third-width">
        <h3>Channel Distribution</h3>
        <Chart type="doughnut" :data="channelChartData" :options="doughnetChartOptions" ref="channelChart" />
      </div>
      <div class="chart-wrapper third-width">
        <h3>Channel Drilldown 1</h3>
        <Chart type="doughnut" :data="channelDrilldown1Data" :options="doughnetChartOptions" ref="channelDrilldown1Chart" />
      </div>
      <div class="chart-wrapper third-width">
        <h3>Channel Drilldown 2</h3>
        <Chart type="doughnut" :data="channelDrilldown2Data" :options="doughnetChartOptions" ref="channelDrilldown2Chart" />
      </div>
      <!-- <div class="chart-wrapper third-width">
        <h3>Conversion Rate</h3>
        <Chart type="doughnut" :data="conversionRateData" :options="doughnetChartOptions" ref="conversionRateChart" />
      </div> -->
    </div>
  </div>
</template>

<script>
import Chart from 'primevue/chart';
import Dropdown from '@/components/inputs/Dropdown.vue';
import { useSalesImpactReportQuery } from '@/composables/useSalesImpactReportQuery';

export default {
  name: 'ChartsView',
  components: {
    Chart,
    Dropdown,
  },
  props: {
    showCharts: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { rawReportData } = useSalesImpactReportQuery();
    return {
      reportData: rawReportData,
    };
  },
  data() {
    return {
      timePeriod: 'daily',
      backgroundColors: [
        '#3366CC',
        '#DC3912',
        '#FF9900',
        '#109618',
        '#990099',
        '#3B3EAC',
        '#0099C6',
        '#DD4477',
        '#66AA00',
        '#B82E2E',
        '#316395',
        '#994499',
        '#22AA99',
        '#AAAA11',
        '#6633CC',
        '#E67300',
        '#8B0707',
        '#329262',
        '#5574A6',
        '#651067',
      ],
      doughnetChartOptions: {
        plugins: { legend: { position: 'right' } },
      },
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
            },
          },
        },
      },
      barChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                return `Average Interactions: ${context.raw.toFixed(2)}`;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            title: {
              display: false,
            },
          },
          y: {
            title: {
              display: false,
            },
          },
        },
      },
      durationBarChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                return `Average Duration: ${context.raw.toFixed(1)} days`;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            title: {
              display: false,
            },
          },
          y: {
            title: {
              display: false,
            },
          },
        },
      },
    };
  },
  computed: {
    dailySourceChartData() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [0] }],
        };
      }

      // Get all leads with valid dates and parse dates
      const leadsWithDates = this.reportData
        .filter((lead) => lead.CREATEDATE)
        .map((lead) => ({
          source: lead.SOURCE,
          parsedDate: parseInt(lead.CREATEDATE),
          formattedDate: this.getFormattedDate(lead.CREATEDATE),
        }));

      // Get unique formatted dates but preserve chronological order
      const uniqueDatesMap = new Map();
      leadsWithDates
        .sort((a, b) => a.parsedDate - b.parsedDate)
        .forEach((lead) => {
          uniqueDatesMap.set(lead.formattedDate, lead.parsedDate);
        });

      // // Convert to array and ensure dates are in chronological order
      const dates = Array.from(uniqueDatesMap.keys());
      const sources = [...new Set(this.reportData.map((lead) => lead.SOURCE))];

      // Create datasets for each source
      const datasets = sources.map((source, index) => {
        const data = dates.map((date) => {
          return leadsWithDates.filter((lead) => lead.formattedDate === date && lead.source === source).length;
        });

        return {
          label: source,
          data: data,
          borderColor: this.backgroundColors[index % this.backgroundColors.length],
          backgroundColor: this.backgroundColors[index % this.backgroundColors.length],
          tension: 0.4,
          fill: false,
        };
      });

      return {
        labels: dates,
        datasets: datasets,
      };
    },
    sourceChartData() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [1] }],
        };
      }

      // Count occurrences of each source
      const sourceCounts = this.reportData.reduce((acc, lead) => {
        const source = lead.SOURCE;
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {});

      // Get unique sources that have data
      const labels = Object.keys(sourceCounts);
      const data = labels.map((source) => sourceCounts[source]);

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: this.backgroundColors,
          },
        ],
      };
    },
    channelChartData() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [1] }],
        };
      }

      const channelCounts = this.reportData.reduce((acc, lead) => {
        try {
          const channelObj = JSON.parse(lead.CHANNEL);
          const channel = channelObj.channel || 'Unknown';
          acc[channel] = (acc[channel] || 0) + 1;
        } catch (error) {
          acc['Unknown'] = (acc['Unknown'] || 0) + 1;
        }
        return acc;
      }, {});

      const labels = Object.keys(channelCounts);
      const data = labels.map((channel) => channelCounts[channel]);

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: this.backgroundColors,
          },
        ],
      };
    },
    channelDrilldown1Data() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [1] }],
        };
      }

      const drilldown1Counts = this.reportData.reduce((acc, lead) => {
        try {
          const channelObj = JSON.parse(lead.CHANNEL);
          const drilldown1 = channelObj.channelDrillDown1 || 'Unknown';
          acc[drilldown1] = (acc[drilldown1] || 0) + 1;
        } catch (error) {
          acc['Unknown'] = (acc['Unknown'] || 0) + 1;
        }
        return acc;
      }, {});

      const labels = Object.keys(drilldown1Counts);
      const data = labels.map((drilldown1) => drilldown1Counts[drilldown1]);

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: this.backgroundColors,
          },
        ],
      };
    },
    channelDrilldown2Data() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [1] }],
        };
      }

      const drilldown2Counts = this.reportData.reduce((acc, lead) => {
        try {
          const channelObj = JSON.parse(lead.CHANNEL);
          const drilldown2 = channelObj.channelDrillDown2 || 'Unknown';
          acc[drilldown2] = (acc[drilldown2] || 0) + 1;
        } catch (error) {
          acc['Unknown'] = (acc['Unknown'] || 0) + 1;
        }
        return acc;
      }, {});

      const labels = Object.keys(drilldown2Counts);
      const data = labels.map((drilldown2) => drilldown2Counts[drilldown2]);

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: this.backgroundColors,
          },
        ],
      };
    },
    conversionRateData() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [1] }],
        };
      }

      // Count leads in each conversion category
      const conversionCounts = this.reportData.reduce((acc, lead) => {
        if (lead.COMPLETE) {
          // Check if it has an amount to determine if won or lost
          if (lead.AMOUNT && parseFloat(lead.AMOUNT) > 0) {
            acc['Closed-Won'] = (acc['Closed-Won'] || 0) + 1;
          } else {
            acc['Closed-Lost'] = (acc['Closed-Lost'] || 0) + 1;
          }
        } else {
          // Any other status is considered open
          acc['Open'] = (acc['Open'] || 0) + 1;
        }
        return acc;
      }, {});

      const labels = Object.keys(conversionCounts);
      const data = labels.map((status) => conversionCounts[status]);

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: this.backgroundColors,
          },
        ],
      };
    },
    avgInteractionsBySourceData() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [0] }],
        };
      }

      // Group leads by source and calculate average interactions
      const sourceInteractions = this.reportData.reduce((acc, lead) => {
        const source = lead.SOURCE || 'Unknown';
        if (!acc[source]) {
          acc[source] = {
            totalInteractions: 0,
            count: 0,
          };
        }

        // Add interactions if the field exists and is a number
        if (lead.TOTALINTERACTIONS && !isNaN(parseInt(lead.TOTALINTERACTIONS))) {
          acc[source].totalInteractions += parseInt(lead.TOTALINTERACTIONS);
          acc[source].count += 1;
        }

        return acc;
      }, {});

      // Calculate average interactions for each source
      const sources = Object.keys(sourceInteractions);
      const averages = sources.map((source) => ({
        source,
        average: sourceInteractions[source].count > 0 ? sourceInteractions[source].totalInteractions / sourceInteractions[source].count : 0,
      }));

      // Sort by average interactions in descending order
      averages.sort((a, b) => b.average - a.average);

      // Format the chart data
      return {
        labels: averages.map((item) => item.source),
        datasets: [
          {
            data: averages.map((item) => item.average),
            backgroundColor: averages.map((_, index) => this.backgroundColors[index % this.backgroundColors.length]),
            borderColor: averages.map((_, index) => this.backgroundColors[index % this.backgroundColors.length]),
            borderWidth: 1,
          },
        ],
      };
    },
    avgLeadDurationBySourceData() {
      if (this.reportData.length === 0) {
        return {
          labels: ['No results'],
          datasets: [{ data: [0] }],
        };
      }

      // Filter for closed leads that have both create and complete dates
      const closedLeads = this.reportData.filter((lead) => lead.COMPLETE && lead.CREATEDATE && lead.COMPLETEDATE);

      if (closedLeads.length === 0) {
        return {
          labels: ['No closed leads'],
          datasets: [{ data: [0] }],
        };
      }

      // Group leads by source and calculate duration
      const sourceDurations = closedLeads.reduce((acc, lead) => {
        const source = lead.SOURCE || 'Unknown';
        if (!acc[source]) {
          acc[source] = {
            totalDuration: 0,
            count: 0,
          };
        }

        try {
          // Convert timestamps to Date objects and calculate duration in days
          const createDate = new Date(parseInt(lead.CREATEDATE));
          const completeDate = new Date(parseInt(lead.COMPLETEDATE));

          // Calculate duration in days (milliseconds to days)
          const durationInDays = (completeDate - createDate) / (1000 * 60 * 60 * 24);

          // Only include valid durations (positive numbers)
          if (!isNaN(durationInDays) && durationInDays >= 0) {
            acc[source].totalDuration += durationInDays;
            acc[source].count += 1;
          }
        } catch (error) {
          // Skip invalid dates
        }

        return acc;
      }, {});

      // Calculate average duration for each source
      const sources = Object.keys(sourceDurations);
      const averages = sources.map((source) => ({
        source,
        average: sourceDurations[source].count > 0 ? sourceDurations[source].totalDuration / sourceDurations[source].count : 0,
      }));

      // Filter out sources with no valid durations
      const validAverages = averages.filter((item) => item.average > 0);

      // Sort by average duration (ascending - shortest first)
      validAverages.sort((a, b) => a.average - b.average);

      // Format the chart data
      return {
        labels: validAverages.map((item) => item.source),
        datasets: [
          {
            data: validAverages.map((item) => item.average),
            backgroundColor: validAverages.map((_, index) => this.backgroundColors[index % this.backgroundColors.length]),
            borderColor: validAverages.map((_, index) => this.backgroundColors[index % this.backgroundColors.length]),
            borderWidth: 1,
          },
        ],
      };
    },
  },
  methods: {
    formatDate(date) {
      try {
        return new Date(parseInt(date)).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        });
      } catch (error) {
        return '-';
      }
    },
    getFormattedDate(date) {
      try {
        const dateObj = new Date(parseInt(date));
        let weekStart;

        switch (this.timePeriod) {
          case 'daily':
            return this.formatDate(date);
          case 'weekly':
            weekStart = new Date(dateObj);
            weekStart.setDate(dateObj.getDate() - dateObj.getDay());
            return `Week of ${this.formatDate(weekStart.getTime())}`;
          case 'monthly':
            return dateObj.toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            });
          default:
            return this.formatDate(date);
        }
      } catch (error) {
        return '-';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@media print {
  .graph-container {
    display: none;
  }
}

.graph-container {
  position: relative;

  .graphs {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2rem;
    justify-content: center;
    align-items: flex-start;
    padding: 2rem;

    .full-width {
      flex: 1 1 100%;
    }

    .half-width {
      flex: 1 1 45%;
    }

    .third-width {
      flex: 1 1 20%;
    }

    .chart-wrapper {
      text-align: center;

      .chart-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
        padding: 0 1rem;

        h3 {
          flex: 1;
        }
      }
    }
  }
}
</style>
